import Swal from "sweetalert2";
import NotificationModule from "./notification_module.js";

const ALERT_MESSAGE =
  "Visit date discrepancy detected between modalities in this visit. Please verify and confirm the correct visit date.";

export default class VisitDateConsistencyModule {
  constructor(notifications = null) {
    this.notifications = notifications || new NotificationModule();
  }

  async checkConsistency({
    studyId,
    subjectId,
    visitId,
    visitDate,
    excludeStackId = null,
    modalityId = null,
    checkCrossVisitConflict = false,
  }) {
    if (!visitId || !visitDate) {
      return { consistent: true, discrepancies: [], stacks: [] };
    }

    const params = new URLSearchParams({
      visit_id: visitId,
      visit_date: visitDate,
    });
    if (checkCrossVisitConflict) {
      params.append("check_cross_visit_conflict", "true");
    }
    if (excludeStackId) {
      params.append("exclude_stack_id", excludeStackId);
    }
    if (modalityId) {
      params.append("modality_id", modalityId);
    }

    const response = await fetch(
      `/studies/${studyId}/subjects/${subjectId}/check_visit_date_consistency?${params}`,
      {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.head
            .querySelector('meta[name="csrf-token"]')
            .getAttribute("content"),
        },
      }
    );

    return response.json();
  }

  formatRailsErrors(payload) {
    if (!payload) return [];
    if (Array.isArray(payload.errors)) return payload.errors;
    if (Array.isArray(payload)) return payload;

    return Object.values(payload).flat();
  }

  showErrorAlert(title, message, useHtml = false) {
    return Swal.fire({
      icon: "error",
      title,
      ...(useHtml ? { html: message } : { text: message }),
    });
  }

  showWarningAlert(message, timer = 2000) {
    return Swal.fire({
      icon: "warning",
      title: message,
      showConfirmButton: false,
      timer,
    });
  }

  async promptVisitDateConflict(message) {
    const result = await Swal.fire({
      icon: "warning",
      title: "Visit date already in use",
      text: message,
      showCancelButton: true,
      confirmButtonText: "Continue",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      allowOutsideClick: false,
    });

    return result.isConfirmed;
  }

  showSuccessAlert(message, timer = 1000) {
    return Swal.fire({
      icon: "success",
      title: message,
      showConfirmButton: false,
      timer,
    });
  }

  buildDiscrepancyHtml(data, proposedVisitDate = null) {
    const rows = (data.discrepancies || [])
      .map(
        (item) =>
          `<tr><td>${item.modality_name}</td><td>${this.formatDate(
            item.visit_date
          )}</td></tr>`
      )
      .join("");

    const proposedLine = proposedVisitDate
      ? `<p class="text-left mb-2"><strong>Proposed visit date:</strong> ${this.formatDate(
          proposedVisitDate
        )}</p>`
      : "";

    return (
      `${proposedLine}` +
      `<div style="max-height: 180px; overflow-y: auto;">` +
      `<table class="table table-sm text-left w-full mb-0">` +
      `<thead><tr><th>Modality</th><th>Visit Date</th></tr></thead>` +
      `<tbody>${rows}</tbody></table></div>`
    );
  }

  buildReasonInputHtml() {
    return (
      '<label class="block text-left mt-3 mb-1"><strong>Reason (required)</strong></label>' +
      '<textarea id="visit-date-discrepancy-reason" class="swal2-textarea w-full" ' +
      'placeholder="Explain why modalities in this visit have different acquisition dates..." rows="3"></textarea>'
    );
  }

  formatDate(value) {
    if (!value) return "";
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
  }

  async promptForDiscrepancyReason(data, proposedVisitDate, options = {}) {
    const {
      confirmButtonText = "Proceed",
      denyButtonText = null,
      cancelButtonText = "Cancel",
      title = data.message || ALERT_MESSAGE,
    } = options;

    const getReason = () => {
      const reason = document
        .getElementById("visit-date-discrepancy-reason")
        ?.value.trim();
      if (!reason) {
        Swal.showValidationMessage(
          "A reason is required when modalities have different visit dates."
        );
        return false;
      }
      return reason;
    };

    const result = await Swal.fire({
      icon: "warning",
      title,
      html: this.buildDiscrepancyHtml(data, proposedVisitDate) + this.buildReasonInputHtml(),
      showCancelButton: true,
      showDenyButton: !!denyButtonText,
      confirmButtonText,
      denyButtonText: denyButtonText || undefined,
      cancelButtonText,
      reverseButtons: true,
      allowOutsideClick: false,
      focusConfirm: false,
      preConfirm: getReason,
      preDeny: denyButtonText ? getReason : undefined,
    });

    if (result.isConfirmed) {
      return {
        action: "confirm",
        reason: result.value,
        discrepancies: data.discrepancies || [],
      };
    }

    if (result.isDenied) {
      return {
        action: "deny",
        reason: result.value,
        discrepancies: data.discrepancies || [],
      };
    }

    return null;
  }

  async showReviewAlert(data, proposedVisitDate) {
    return this.promptForDiscrepancyReason(data, proposedVisitDate, {
      confirmButtonText: "Confirm & Sync All",
      denyButtonText: "Acknowledge",
      cancelButtonText: "Cancel",
    });
  }

  async recordDiscrepancyReason({
    studyId,
    subjectId,
    stackId,
    visitDate,
    reason,
  }) {
    const response = await fetch(
      `/studies/${studyId}/subjects/${subjectId}/record_visit_date_discrepancy_reason`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.head
            .querySelector('meta[name="csrf-token"]')
            .getAttribute("content"),
        },
        body: JSON.stringify({
          stack_id: stackId,
          proposed_visit_date: visitDate,
          discrepancy_reason: reason,
        }),
      }
    );

    return response.json();
  }

  async syncVisitDates({
    studyId,
    subjectId,
    visitId,
    confirmedVisitDate,
    discrepancyReason = null,
  }) {
    const response = await fetch(
      `/studies/${studyId}/subjects/${subjectId}/sync_visit_dates`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.head
            .querySelector('meta[name="csrf-token"]')
            .getAttribute("content"),
        },
        body: JSON.stringify({
          visit_id: visitId,
          confirmed_visit_date: confirmedVisitDate,
          discrepancy_reason: discrepancyReason,
        }),
      }
    );

    return response.json();
  }

  async handleCreateSubmit({
    studyId,
    subjectId,
    visitId,
    visitDate,
    modalityId,
    excludeStackId = null,
  }) {
    const data = await this.checkConsistency({
      studyId,
      subjectId,
      visitId,
      visitDate,
      excludeStackId,
      modalityId,
      checkCrossVisitConflict: true,
    });

    if (data.visit_date_conflict) {
      const confirmed = await this.promptVisitDateConflict(data.message);
      if (!confirmed) {
        return false;
      }

      return { proceed: true, confirmVisitDateConflict: true };
    }

    if (data.consistent) {
      return { proceed: true };
    }

    const reasonResult = await this.promptForDiscrepancyReason(data, visitDate);
    if (!reasonResult) {
      return false;
    }

    return {
      proceed: true,
      reason: reasonResult.reason,
      discrepancies: reasonResult.discrepancies,
    };
  }

  async handleReviewLoad({
    studyId,
    subjectId,
    visitId,
    visitDate,
    stackId = null,
    modalityId = null,
  }) {
    const data = await this.checkConsistency({
      studyId,
      subjectId,
      visitId,
      visitDate,
      excludeStackId: stackId,
      modalityId,
    });

    if (data.consistent) {
      return;
    }

    const reasonResult = await this.showReviewAlert(data, visitDate);
    if (!reasonResult) {
      return;
    }

    if (reasonResult.action === "confirm") {
      const syncResult = await this.syncVisitDates({
        studyId,
        subjectId,
        visitId,
        confirmedVisitDate: visitDate,
        discrepancyReason: reasonResult.reason,
      });

      if (syncResult.status === "success") {
        this.notifications.success(syncResult.message);
        window.location.reload();
      } else {
        this.notifications.error(
          syncResult.message || "Failed to sync visit dates."
        );
      }
      return;
    }

    const recordResult = await this.recordDiscrepancyReason({
      studyId,
      subjectId,
      stackId,
      visitDate,
      reason: reasonResult.reason,
    });

    if (recordResult.status === "success") {
      this.notifications.success(recordResult.message);
    } else {
      this.notifications.error(
        recordResult.message || "Failed to record discrepancy reason."
      );
    }
  }

  async handleDateUpdate({
    studyId,
    subjectId,
    visitId,
    visitDate,
    stackId,
    modalityId,
    updatePayload,
    updateUrl,
  }) {
    const data = await this.checkConsistency({
      studyId,
      subjectId,
      visitId,
      visitDate,
      excludeStackId: stackId,
      modalityId: modalityId || updatePayload?.modalityId,
    });

    if (data.consistent) {
      return this.performDateUpdate(updateUrl, updatePayload);
    }

    const reasonResult = await this.promptForDiscrepancyReason(data, visitDate, {
      confirmButtonText: "Confirm & Sync All",
      denyButtonText: "Update This Stack Only",
    });

    if (!reasonResult) {
      return null;
    }

    return this.performDateUpdate(updateUrl, {
      ...updatePayload,
      discrepancy_reason: reasonResult.reason,
      syncAllModalities: reasonResult.action === "confirm",
    });
  }

  setConfirmVisitDateConflictOnForm(form) {
    if (!form) return;

    let input = form.querySelector(
      'input[name="stack[confirm_visit_date_conflict]"]'
    );
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "stack[confirm_visit_date_conflict]";
      form.appendChild(input);
    }
    input.value = "true";
  }

  setDiscrepancyReasonOnForm(form, reason) {
    if (!form || !reason) return;

    let input = form.querySelector(
      'input[name="stack[visit_date_discrepancy_reason]"]'
    );
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "stack[visit_date_discrepancy_reason]";
      form.appendChild(input);
    }
    input.value = reason;
  }

  async performDateUpdate(updateUrl, payload) {
    const response = await fetch(updateUrl, {
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRF-Token": document.head
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      method: "PATCH",
      body: JSON.stringify(payload),
    });

    return response.json();
  }
}
